import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const VISIBLE_ACTION_NEEDED_ITEMS = 2;

// Item heights vary with their text, so the cap is measured off the rendered rows.
export const useVisibleItemsCap = (itemSelector: string, contentKey: unknown) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [listMaxHeight, setListMaxHeight] = useState<number | null>(null);

  const measureListCap = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    const items = list.querySelectorAll<HTMLElement>(itemSelector);
    if (items.length <= VISIBLE_ACTION_NEEDED_ITEMS) {
      setListMaxHeight(null);
      return;
    }

    const lastVisible = items[VISIBLE_ACTION_NEEDED_ITEMS - 1];
    setListMaxHeight(lastVisible.offsetTop + lastVisible.offsetHeight);
  }, [itemSelector]);

  useLayoutEffect(() => {
    measureListCap();
  }, [measureListCap, contentKey]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !contentRef.current) return;

    // Observe the inner content — the capped outer box never re-fires on height-only re-wraps.
    const observer = new ResizeObserver(() => measureListCap());
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [measureListCap, contentKey]);

  return { listRef, contentRef, listMaxHeight };
};
